import React, { useEffect } from "react";
import { v4 } from "uuid";

import { useChatWorkflowShared } from "@intelligems/ig-plugin/builder";
import {
  ChatWorkflowRequestBodyDto,
  ToolResultOutput,
  UiStreamAction,
} from "@intelligems/ig-types";
import { aiApiClient } from "@src/api/ai-api";
import { createUserMessage } from "@src/features/ai/components/AiChat/utils/workflowUtils";
import { ExecutionPanel } from "@src/features/ai/components/WorkflowEditor/execution/ExecutionPanel";
import { useDispatch, useSelector } from "@src/store";
import {
  clearAiChat,
  getAiChat,
  handleAiChatAction,
} from "@src/store/slices/aiChat";

import { OnAiChatSend } from "@src/features/ai/components/WorkflowEditor/execution/types";

import type { WorkspaceAiChatConfig } from "../../domainConfig";
import type { WorkspaceDependency } from "../../types";

interface WorkspaceAiChatProps {
  /** Workflow id, feature tag, and the ui-action that carries generated code. */
  config: WorkspaceAiChatConfig;
  editorValue: string;
  runtimeContract: string;
  dependencies: WorkspaceDependency[];
  availableSecretNames: string[];
  onApplySource: (source: string) => void;
  onPendingChange: (isPending: boolean) => void;
}

/**
 * Pulls generated source out of a `ui-action` tool result. The action name is
 * per-domain; the envelope shape is not.
 */
const extractGeneratedSource = (
  action: UiStreamAction,
  config: WorkspaceAiChatConfig
): string | null => {
  if (
    action.type !== "addStreamPart" ||
    action.streamPart.type !== "tool-output-available"
  ) {
    return null;
  }

  const output = action.streamPart.output as ToolResultOutput;
  const value = output.value;
  if (!value || typeof value !== "object" || !("results" in value)) {
    return null;
  }

  const results = (value as { results: unknown }).results;
  if (!results || typeof results !== "object") return null;

  const record = results as Record<string, unknown>;
  if (
    record.type !== "ui-action" ||
    record.action !== config.uiActionName ||
    typeof record.source !== "string"
  ) {
    return null;
  }

  const decoded = config.decodeGeneratedSource?.(record.source);
  return decoded !== undefined && decoded.length > 0 ? decoded : record.source;
};

export const WorkspaceAiChat = ({
  config,
  editorValue,
  runtimeContract,
  dependencies,
  availableSecretNames,
  onApplySource,
  onPendingChange,
}: WorkspaceAiChatProps) => {
  const dispatch = useDispatch();
  const aiChat = useSelector(getAiChat);

  const chatWorkflow = useChatWorkflowShared(
    aiApiClient,
    (action: UiStreamAction) => {
      const source = extractGeneratedSource(action, config);
      if (source !== null) {
        onApplySource(source);
      }
      dispatch(handleAiChatAction(action));
    }
  );

  useEffect(() => {
    onPendingChange(chatWorkflow.isPending);
    return () => onPendingChange(false);
  }, [chatWorkflow.isPending, onPendingChange]);

  const handleNewChat = () => {
    dispatch(clearAiChat());
    onPendingChange(false);
  };

  const buildVariables = () => ({
    source: editorValue,
    runtimeContract,
    dependencies,
    availableSecretNames,
    ...config.extraVariables,
  });

  const onSend: OnAiChatSend = {
    chat: (payload: ChatWorkflowRequestBodyDto) => {
      if (!payload.input) return;

      const inputMessageId = v4();
      const { message } = createUserMessage(payload.input);
      if (message) {
        message.id = inputMessageId;
      }
      const nodeId = v4();
      const turnId = v4();
      const variables = buildVariables();

      if (!aiChat.sessionId) {
        const sessionId = v4();
        const actions: UiStreamAction[] = [
          {
            type: "initialize",
            workflowId: config.workflowId,
            sessionId,
            nodeId,
          },
        ];

        if (message) {
          actions.push({
            type: "addMessage",
            turnId,
            message,
            nodeId,
            nodeName: "User Input",
          });
        }

        dispatch(handleAiChatAction(actions));

        void chatWorkflow.mutate({
          workflowId: config.workflowId,
          sessionId,
          input: payload.input,
          inputMessageId,
          initialContext: {
            variables,
          },
        });
      } else {
        if (message) {
          dispatch(
            handleAiChatAction({
              type: "addMessage",
              turnId,
              message,
              nodeId,
              nodeName: "User Input",
            })
          );
        }

        void chatWorkflow.mutate({
          sessionId: payload.sessionId || aiChat.sessionId,
          input: payload.input,
          inputMessageId,
          variables,
          restartFromFirstNode: true,
        });
      }
    },
    isPending: chatWorkflow.isPending,
  };

  return (
    <ExecutionPanel
      feature={config.feature}
      onNewChat={handleNewChat}
      onSend={onSend}
      workflowId={config.workflowId}
      showSystemMessages={false}
      showToolCallOutput={false}
      showToolsAsTasks={true}
      clearReasoningOnMessage={true}
      showReasoningMessages={false}
      showConditionNodes={false}
    />
  );
};
